import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'

export function Auth() {
  const { signIn, signUp } = useAuth()
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const { error } = isSignUp
      ? await signUp(email, password)
      : await signIn(email, password)

    if (error) {
      setError(error.message)
    }
    setLoading(false)
  }

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' }

    let strength = 0
    if (password.length >= 8) strength++
    if (password.length >= 12) strength++
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++
    if (/\d/.test(password)) strength++
    if (/[^a-zA-Z0-9]/.test(password)) strength++

    if (strength <= 1) return { strength: 20, label: 'Weak', color: 'bg-error' }
    if (strength === 2) return { strength: 40, label: 'Fair', color: 'bg-warning' }
    if (strength === 3) return { strength: 60, label: 'Good', color: 'bg-secondary' }
    if (strength === 4) return { strength: 80, label: 'Strong', color: 'bg-primary' }
    return { strength: 100, label: 'Very Strong', color: 'bg-success' }
  }

  const passwordStrength = isSignUp ? getPasswordStrength(password) : null

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-soft-light filter blur-xl opacity-50 animate-blob"
          style={{ background: 'rgba(var(--accent-primary-rgb), 0.4)' }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-soft-light filter blur-xl opacity-50 animate-blob animation-delay-2000"
          style={{ background: 'rgba(var(--accent-secondary-rgb), 0.4)' }}
        />
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full mix-blend-soft-light filter blur-xl opacity-50 animate-blob animation-delay-4000"
          style={{ background: 'rgba(var(--accent-primary-rgb), 0.4)' }}
        />
      </div>

      {/* Main Card */}
      <div className="max-w-md w-full relative">
        {/* Glass Morphism Card */}
        <div className="bg-background/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-border/50 p-8 transform transition-all duration-300">
          {/* App Icon/Logo */}
          <div className="flex justify-center mb-6">
            <div
              className="p-4 rounded-2xl shadow-lg transform transition-transform hover:scale-110 duration-300"
              style={{ background: 'var(--accent-primary)' }}
            >
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-bold mb-2"
              style={{ color: 'var(--text)' }}
            >
              today over yesterday
            </h1>
            <p className="text-sm text-foreground-muted">
              {isSignUp ? 'Create your account to start tracking' : 'Welcome back! Sign in to continue'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-background-muted border-2 border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground transition-all duration-300 placeholder-foreground-muted"
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder={isSignUp ? 'Create a strong password' : 'Enter your password'}
                  className="w-full px-4 py-3 pr-12 bg-background-muted border-2 border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground transition-all duration-300 placeholder-foreground-muted"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors"
                >
 {showPassword ?'':''}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {isSignUp && password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 bg-background-muted rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full ${passwordStrength.color} transition-all duration-300`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground-muted">
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-muted">
                    {password.length < 8 && 'Use 8+ characters for better security'}
                  </p>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-error-10 border-2 border-error text-error px-4 py-3 rounded-xl animate-shake">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{error}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full text-black font-bold py-4 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
              style={{ background: 'var(--accent-primary)' }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <span>{isSignUp ? 'Create Account' : 'Sign In'}</span>
              )}
            </button>
          </form>

          {/* Toggle Sign Up/Sign In */}
          <div className="mt-6 pt-6 border-t border-border">
            <button
              onClick={() => {
                setIsSignUp(!isSignUp)
                setError(null)
                setPassword('')
              }}
              className="w-full text-sm font-medium text-foreground-muted hover:text-primary transition-colors"
            >
              {isSignUp ? (
                <span>Already have an account? <span className="text-primary font-bold">Sign in →</span></span>
              ) : (
                <span>Don't have an account? <span className="text-secondary font-bold">Sign up →</span></span>
              )}
            </button>
          </div>

          {/* Additional Info for Sign Up */}
          {isSignUp && (
            <div className="mt-6 bg-primary-10 border border-primary-30 rounded-xl p-4">
              <p className="text-xs text-foreground text-center">
                <strong>Free forever.</strong> Track unlimited entries, supplements, and health metrics.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-foreground-muted">
            Secure authentication powered by Supabase
          </p>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-5px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(5px);
          }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `}</style>
    </div>
  )
}
